import React, { useState, Suspense } from 'react';
import { LayersIcon, HeartIcon, SparklesIcon, SearchIcon, SkullIcon, RocketIcon, BookOpenIcon } from 'lucide-react';
import { useDarkClasses } from '../../../hooks/useDarkClasses';
import { genreToModuleKey } from './genreModuleData';
import type { PM } from './useProductManager';
// Genre Modules
import { RomanceModule, RomanceModuleCompact } from './genre-modules/RomanceModule';
import { FantasyModule, FantasyModuleCompact } from './genre-modules/FantasyModule';
import { ThrillerMysteryModule, ThrillerMysteryModuleCompact } from './genre-modules/ThrillerMysteryModule';
import { HorrorModule, HorrorModuleCompact } from './genre-modules/HorrorModule';
import { SciFiModule, SciFiModuleCompact } from './genre-modules/SciFiModule';
import { ContemporaryModule, ContemporaryModuleCompact } from './genre-modules/ContemporaryModule';
import { CharactersRepresentationSection } from './genre-modules/CharactersRepresentationSection';
import { WorldSettingSection } from './genre-modules/WorldSettingSection';
interface GenreDetailsSectionProps {
  pm: PM;
}
// ─── Genre metadata state per genre ───
interface RomanceState {
  pairingType: string;
  tropes: string[];
  characterPairings: string[];
  endingType: string;
  spiceLevel: number;
  burnLevel: number;
  darknessLevel: number;
  warnings: string[];
}
interface FantasyState {
  subtype: string;
  magicSystem: string;
  worldStructure: string;
  storyEngines: string[];
  creatureTags: string[];
  tone: string;
}
interface ThrillerMysteryState {
  subtype: string;
  investigationType: string;
  threatTypes: string[];
  storyEngines: string[];
  suspenseIntensity: string;
}
interface HorrorState {
  subtype: string;
  fearMechanisms: string[];
  storyEngines: string[];
  intensity: string;
  extremeLayers: string[];
}
interface SciFiState {
  subtype: string;
  coreConcepts: string[];
  scale: string;
  scienceDepth: string;
  storyEngines: string[];
}
interface ContemporaryState {
  subtype: string;
  conflictTypes: string[];
  storyEngines: string[];
  emotionalWeight: string;
}
const GENRE_ICONS: Record<string, React.ReactNode> = {
  romance: <HeartIcon className="w-4 h-4 text-pink-500" data-id="element-20749" data-mp="mca3kmdsiqaf" />,
  fantasy: <SparklesIcon className="w-4 h-4 text-purple-500" data-id="element-20750" data-mp="1128zeihvhpmq" />,
  'thriller-mystery': <SearchIcon className="w-4 h-4 text-amber-500" data-id="element-20751" data-mp="113k5wq2pdbhj" />,
  horror: <SkullIcon className="w-4 h-4 text-red-500" data-id="element-20752" data-mp="w34qzu2rvvxg" />,
  scifi: <RocketIcon className="w-4 h-4 text-cyan-500" data-id="element-20753" data-mp="8cs85o5zoes3" />,
  contemporary: <BookOpenIcon className="w-4 h-4 text-emerald-500" data-id="element-20754" data-mp="8f4pq07u4n4m" />
};
const GENRE_LABELS: Record<string, string> = {
  romance: 'Romance',
  fantasy: 'Fantasy',
  'thriller-mystery': 'Thriller / Mystery',
  horror: 'Horror',
  scifi: 'Sci-Fi',
  contemporary: 'Contemporary / General Fiction'
};
const GENRE_BORDER_COLORS: Record<string, string> = {
  romance: 'border-pink-200 dark:border-pink-900/50',
  fantasy: 'border-purple-200 dark:border-purple-900/50',
  'thriller-mystery': 'border-amber-200 dark:border-amber-900/50',
  horror: 'border-red-200 dark:border-red-900/50',
  scifi: 'border-cyan-200 dark:border-cyan-900/50',
  contemporary: 'border-emerald-200 dark:border-emerald-900/50'
};
export const GenreDetailsSection: React.FC<GenreDetailsSectionProps> = ({
  pm
}) => {
  const dk = useDarkClasses();
  const {
    bookForm,
    secondaryGenres
  } = pm;
  const primaryGenre = bookForm.genre || '';
  const primaryKey = genreToModuleKey(primaryGenre);
  // ─── Genre metadata state ───
  const [romanceState, setRomanceState] = useState<RomanceState>({
    pairingType: '',
    tropes: [],
    characterPairings: [],
    endingType: '',
    spiceLevel: 0,
    burnLevel: 0,
    darknessLevel: 0,
    warnings: []
  });
  const [fantasyState, setFantasyState] = useState<FantasyState>({
    subtype: '',
    magicSystem: '',
    worldStructure: '',
    storyEngines: [],
    creatureTags: [],
    tone: ''
  });
  const [thrillerState, setThrillerState] = useState<ThrillerMysteryState>({
    subtype: '',
    investigationType: '',
    threatTypes: [],
    storyEngines: [],
    suspenseIntensity: ''
  });
  const [horrorState, setHorrorState] = useState<HorrorState>({
    subtype: '',
    fearMechanisms: [],
    storyEngines: [],
    intensity: '',
    extremeLayers: []
  });
  const [scifiState, setScifiState] = useState<SciFiState>({
    subtype: '',
    coreConcepts: [],
    scale: '',
    scienceDepth: '',
    storyEngines: []
  });
  const [contemporaryState, setContemporaryState] = useState<ContemporaryState>({
    subtype: '',
    conflictTypes: [],
    storyEngines: [],
    emotionalWeight: ''
  });
  // Crossover states (separate from primary)
  const [crossoverRomance, setCrossoverRomance] = useState<{
    pairingType: string;
    tropes: string[];
    endingType: string;
  }>({
    pairingType: '',
    tropes: [],
    endingType: ''
  });
  const [crossoverFantasy, setCrossoverFantasy] = useState<{
    subtype: string;
    magicSystem: string;
    creatureTags: string[];
    worldStructure: string;
  }>({
    subtype: '',
    magicSystem: '',
    creatureTags: [],
    worldStructure: ''
  });
  const [crossoverThriller, setCrossoverThriller] = useState<{
    subtype: string;
    threatTypes: string[];
    suspenseIntensity: string;
  }>({
    subtype: '',
    threatTypes: [],
    suspenseIntensity: ''
  });
  const [crossoverHorror, setCrossoverHorror] = useState<{
    subtype: string;
    fearMechanisms: string[];
    intensity: string;
  }>({
    subtype: '',
    fearMechanisms: [],
    intensity: ''
  });
  const [crossoverScifi, setCrossoverScifi] = useState<{
    subtype: string;
    coreConcepts: string[];
    scale: string;
  }>({
    subtype: '',
    coreConcepts: [],
    scale: ''
  });
  const [crossoverContemporary, setCrossoverContemporary] = useState<{
    subtype: string;
    conflictTypes: string[];
    emotionalWeight: string;
  }>({
    subtype: '',
    conflictTypes: [],
    emotionalWeight: ''
  });
  if (!primaryGenre) return null;
  // ─── Render primary module ───
  const renderPrimaryModule = () => {
    switch (primaryKey) {
      case 'romance':
        return <RomanceModule pairingType={romanceState.pairingType} setPairingType={v => setRomanceState(s => ({
          ...s,
          pairingType: v
        }))} tropes={romanceState.tropes} setTropes={fn => setRomanceState(s => ({
          ...s,
          tropes: typeof fn === 'function' ? fn(s.tropes) : fn
        }))} characterPairings={romanceState.characterPairings} setCharacterPairings={fn => setRomanceState(s => ({
          ...s,
          characterPairings: typeof fn === 'function' ? fn(s.characterPairings) : fn
        }))} endingType={romanceState.endingType} setEndingType={v => setRomanceState(s => ({
          ...s,
          endingType: v
        }))} spiceLevel={romanceState.spiceLevel} setSpiceLevel={v => setRomanceState(s => ({
          ...s,
          spiceLevel: v
        }))} burnLevel={romanceState.burnLevel} setBurnLevel={v => setRomanceState(s => ({
          ...s,
          burnLevel: v
        }))} darknessLevel={romanceState.darknessLevel} setDarknessLevel={v => setRomanceState(s => ({
          ...s,
          darknessLevel: v
        }))} warnings={romanceState.warnings} setWarnings={fn => setRomanceState(s => ({
          ...s,
          warnings: typeof fn === 'function' ? fn(s.warnings) : fn
        }))} data-id="element-20755" data-mp="2w36t7opq92n8" />;
      case 'fantasy':
        return <FantasyModule subtype={fantasyState.subtype} setSubtype={v => setFantasyState(s => ({
          ...s,
          subtype: v
        }))} magicSystem={fantasyState.magicSystem} setMagicSystem={v => setFantasyState(s => ({
          ...s,
          magicSystem: v
        }))} worldStructure={fantasyState.worldStructure} setWorldStructure={v => setFantasyState(s => ({
          ...s,
          worldStructure: v
        }))} storyEngines={fantasyState.storyEngines} setStoryEngines={fn => setFantasyState(s => ({
          ...s,
          storyEngines: typeof fn === 'function' ? fn(s.storyEngines) : fn
        }))} creatureTags={fantasyState.creatureTags} setCreatureTags={fn => setFantasyState(s => ({
          ...s,
          creatureTags: typeof fn === 'function' ? fn(s.creatureTags) : fn
        }))} tone={fantasyState.tone} setTone={v => setFantasyState(s => ({
          ...s,
          tone: v
        }))} data-id="element-20756" data-mp="492ezdhnnsue" />;
      case 'thriller-mystery':
        return <ThrillerMysteryModule subtype={thrillerState.subtype} setSubtype={v => setThrillerState(s => ({
          ...s,
          subtype: v
        }))} investigationType={thrillerState.investigationType} setInvestigationType={v => setThrillerState(s => ({
          ...s,
          investigationType: v
        }))} threatTypes={thrillerState.threatTypes} setThreatTypes={fn => setThrillerState(s => ({
          ...s,
          threatTypes: typeof fn === 'function' ? fn(s.threatTypes) : fn
        }))} storyEngines={thrillerState.storyEngines} setStoryEngines={fn => setThrillerState(s => ({
          ...s,
          storyEngines: typeof fn === 'function' ? fn(s.storyEngines) : fn
        }))} suspenseIntensity={thrillerState.suspenseIntensity} setSuspenseIntensity={v => setThrillerState(s => ({
          ...s,
          suspenseIntensity: v
        }))} data-id="element-20757" data-mp="ujkz1jbo4mcm" />;
      case 'horror':
        return <HorrorModule subtype={horrorState.subtype} setSubtype={v => setHorrorState(s => ({
          ...s,
          subtype: v
        }))} fearMechanisms={horrorState.fearMechanisms} setFearMechanisms={fn => setHorrorState(s => ({
          ...s,
          fearMechanisms: typeof fn === 'function' ? fn(s.fearMechanisms) : fn
        }))} storyEngines={horrorState.storyEngines} setStoryEngines={fn => setHorrorState(s => ({
          ...s,
          storyEngines: typeof fn === 'function' ? fn(s.storyEngines) : fn
        }))} intensity={horrorState.intensity} setIntensity={v => setHorrorState(s => ({
          ...s,
          intensity: v
        }))} extremeLayers={horrorState.extremeLayers} setExtremeLayers={fn => setHorrorState(s => ({
          ...s,
          extremeLayers: typeof fn === 'function' ? fn(s.extremeLayers) : fn
        }))} data-id="element-20758" data-mp="8s1hpnu27e4r" />;
      case 'scifi':
        return <SciFiModule subtype={scifiState.subtype} setSubtype={v => setScifiState(s => ({
          ...s,
          subtype: v
        }))} coreConcepts={scifiState.coreConcepts} setCoreConcepts={fn => setScifiState(s => ({
          ...s,
          coreConcepts: typeof fn === 'function' ? fn(s.coreConcepts) : fn
        }))} scale={scifiState.scale} setScale={v => setScifiState(s => ({
          ...s,
          scale: v
        }))} scienceDepth={scifiState.scienceDepth} setScienceDepth={v => setScifiState(s => ({
          ...s,
          scienceDepth: v
        }))} storyEngines={scifiState.storyEngines} setStoryEngines={fn => setScifiState(s => ({
          ...s,
          storyEngines: typeof fn === 'function' ? fn(s.storyEngines) : fn
        }))} data-id="element-20759" data-mp="y3uudsjrx2qg" />;
      case 'contemporary':
        return <ContemporaryModule subtype={contemporaryState.subtype} setSubtype={v => setContemporaryState(s => ({
          ...s,
          subtype: v
        }))} conflictTypes={contemporaryState.conflictTypes} setConflictTypes={fn => setContemporaryState(s => ({
          ...s,
          conflictTypes: typeof fn === 'function' ? fn(s.conflictTypes) : fn
        }))} storyEngines={contemporaryState.storyEngines} setStoryEngines={fn => setContemporaryState(s => ({
          ...s,
          storyEngines: typeof fn === 'function' ? fn(s.storyEngines) : fn
        }))} emotionalWeight={contemporaryState.emotionalWeight} setEmotionalWeight={v => setContemporaryState(s => ({
          ...s,
          emotionalWeight: v
        }))} data-id="element-20760" data-mp="31iewalr487ih" />;
      default:
        return <div className={`p-4 rounded-lg ${dk.surfaceMuted}`} data-id="element-20761" data-mp="24q8hganck1f6">
            <p className={`text-sm ${dk.textMuted}`} data-id="element-20762" data-mp="28temxg2mxokl">
              Structured metadata for "{primaryGenre}" is coming soon. Your book
              will still be discoverable through universal tags and themes.
            </p>
          </div>;
    }
  };
  // ─── Render compact crossover module ───
  const renderCrossoverModule = (genre: string) => {
    const key = genreToModuleKey(genre);
    if (key === primaryKey) return null; // Don't show crossover for primary genre
    switch (key) {
      case 'romance':
        return <RomanceModuleCompact pairingType={crossoverRomance.pairingType} setPairingType={v => setCrossoverRomance(s => ({
          ...s,
          pairingType: v
        }))} tropes={crossoverRomance.tropes} setTropes={fn => setCrossoverRomance(s => ({
          ...s,
          tropes: typeof fn === 'function' ? fn(s.tropes) : fn
        }))} endingType={crossoverRomance.endingType} setEndingType={v => setCrossoverRomance(s => ({
          ...s,
          endingType: v
        }))} data-id="element-20763" data-mp="737f4gnjns0i" />;
      case 'fantasy':
        return <FantasyModuleCompact subtype={crossoverFantasy.subtype} setSubtype={v => setCrossoverFantasy(s => ({
          ...s,
          subtype: v
        }))} magicSystem={crossoverFantasy.magicSystem} setMagicSystem={v => setCrossoverFantasy(s => ({
          ...s,
          magicSystem: v
        }))} creatureTags={crossoverFantasy.creatureTags} setCreatureTags={fn => setCrossoverFantasy(s => ({
          ...s,
          creatureTags: typeof fn === 'function' ? fn(s.creatureTags) : fn
        }))} worldStructure={crossoverFantasy.worldStructure} setWorldStructure={v => setCrossoverFantasy(s => ({
          ...s,
          worldStructure: v
        }))} data-id="element-20764" data-mp="31jrpypraprru" />;
      case 'thriller-mystery':
        return <ThrillerMysteryModuleCompact subtype={crossoverThriller.subtype} setSubtype={v => setCrossoverThriller(s => ({
          ...s,
          subtype: v
        }))} threatTypes={crossoverThriller.threatTypes} setThreatTypes={fn => setCrossoverThriller(s => ({
          ...s,
          threatTypes: typeof fn === 'function' ? fn(s.threatTypes) : fn
        }))} suspenseIntensity={crossoverThriller.suspenseIntensity} setSuspenseIntensity={v => setCrossoverThriller(s => ({
          ...s,
          suspenseIntensity: v
        }))} data-id="element-20765" data-mp="25fixf43wl733" />;
      case 'horror':
        return <HorrorModuleCompact subtype={crossoverHorror.subtype} setSubtype={v => setCrossoverHorror(s => ({
          ...s,
          subtype: v
        }))} fearMechanisms={crossoverHorror.fearMechanisms} setFearMechanisms={fn => setCrossoverHorror(s => ({
          ...s,
          fearMechanisms: typeof fn === 'function' ? fn(s.fearMechanisms) : fn
        }))} intensity={crossoverHorror.intensity} setIntensity={v => setCrossoverHorror(s => ({
          ...s,
          intensity: v
        }))} data-id="element-20766" data-mp="2oehnp4blqdz9" />;
      case 'scifi':
        return <SciFiModuleCompact subtype={crossoverScifi.subtype} setSubtype={v => setCrossoverScifi(s => ({
          ...s,
          subtype: v
        }))} coreConcepts={crossoverScifi.coreConcepts} setCoreConcepts={fn => setCrossoverScifi(s => ({
          ...s,
          coreConcepts: typeof fn === 'function' ? fn(s.coreConcepts) : fn
        }))} scale={crossoverScifi.scale} setScale={v => setCrossoverScifi(s => ({
          ...s,
          scale: v
        }))} data-id="element-20767" data-mp="3h210zc8dx0i1" />;
      case 'contemporary':
        return <ContemporaryModuleCompact subtype={crossoverContemporary.subtype} setSubtype={v => setCrossoverContemporary(s => ({
          ...s,
          subtype: v
        }))} conflictTypes={crossoverContemporary.conflictTypes} setConflictTypes={fn => setCrossoverContemporary(s => ({
          ...s,
          conflictTypes: typeof fn === 'function' ? fn(s.conflictTypes) : fn
        }))} emotionalWeight={crossoverContemporary.emotionalWeight} setEmotionalWeight={v => setCrossoverContemporary(s => ({
          ...s,
          emotionalWeight: v
        }))} data-id="element-20768" data-mp="26xraaiw43mhi" />;
      default:
        return null;
    }
  };
  // Filter secondary genres to only those with modules different from primary
  const crossoverGenres = secondaryGenres.filter(g => genreToModuleKey(g) !== primaryKey);
  return <div className={`p-6 rounded-xl shadow-sm ${dk.card}`} data-id="element-20769" data-mp="3h8m518gz3pw4">
      {/* Section Header */}
      <h2 className={`text-lg font-bold mb-2 flex items-center ${dk.textHeading}`} data-id="element-20770" data-mp="3cgly2w2hjwq1">
        <LayersIcon className="w-5 h-5 mr-2 text-indigo-500" data-id="element-20771" data-mp="37gzocwlv8ylz" />
        Genre Details
      </h2>
      <p className={`text-xs mb-6 ${dk.textMuted}`} data-id="element-20772" data-mp="33gywa95ugowg">
        Genre-specific metadata helps readers discover books more accurately.
        Only fields relevant to the selected genres will appear.
      </p>

      {/* ─── Primary Genre Module ─── */}
      <div className="mb-6" data-id="element-20773" data-mp="2tk2x4u1u5f4k">
        <div className="flex items-center gap-2 mb-4" data-id="element-20774" data-mp="1907jfbu10650">
          {GENRE_ICONS[primaryKey] || <LayersIcon className="w-4 h-4 text-gray-500" data-id="element-20775" data-mp="1dys8qg7qp5eo" />}
          <h3 className={`text-md font-bold ${dk.textPrimary}`} data-id="element-20776" data-mp="1e14znivh6q3v">
            {GENRE_LABELS[primaryKey] || primaryGenre} Module
          </h3>
          <span className="text-xs font-semibold px-2 py-0.5 bg-indigo-50 text-indigo-600 dark:bg-indigo-900/20 dark:text-indigo-400 rounded-full" data-id="element-20777" data-mp="1n08jwq6dpbjy">
            Primary
          </span>
        </div>
        {renderPrimaryModule()}
      </div>

      {/* ─── Secondary Genre Crossover Modules ─── */}
      {crossoverGenres.length > 0 && <div className={`pt-6 border-t ${dk.border} space-y-4`} data-id="element-20778" data-mp="z8l3j26qtivx">
          <h3 className={`text-sm font-bold uppercase tracking-wider ${dk.textMuted}`} data-id="element-20779" data-mp="1414cfluhjpue">
            Crossover Modules
          </h3>
          {crossoverGenres.map(genre => {
        const key = genreToModuleKey(genre);
        const module = renderCrossoverModule(genre);
        if (!module) return null;
        return <div key={genre} className={`p-5 rounded-xl border-2 ${GENRE_BORDER_COLORS[key] || dk.border} ${dk.surfaceMuted}`} data-id="element-20780" data-mp="19we3yfqs8c7z">
                <div className="flex items-center gap-2 mb-4" data-id="element-20781" data-mp="1ond91gsesimt">
                  {GENRE_ICONS[key] || <LayersIcon className="w-4 h-4 text-gray-500" data-id="element-20782" data-mp="1joabsiio7gpe" />}
                  <h4 className={`text-sm font-bold ${dk.textPrimary}`} data-id="element-20783" data-mp="vwv7ik7c2pbi">
                    {GENRE_LABELS[key] || genre}
                  </h4>
                  <span className="text-[10px] font-semibold px-2 py-0.5 bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400 rounded-full" data-id="element-20784" data-mp="14xt2ye3phoih">
                    Crossover
                  </span>
                </div>
                {module}
              </div>;
      })}
        </div>}

      {/* ─── Universal Sections (Characters & Setting) ─── */}
      <div className="mt-8 space-y-8" data-id="element-20785" data-mp="2judtkaapyobk">
        <CharactersRepresentationSection primaryGenre={primaryGenre} data-id="element-20786" data-mp="2jtci44psjztb" />
        <WorldSettingSection data-id="element-20787" data-mp="2ot7bcex1h1hj" />
      </div>
    </div>;
};